import * as _ from 'lodash'
import {IBusinessObject, IBusinessObjectChanges, IBusinessObjectChangesOptions} from '../PA.Core/PA.Core.IF.BO';
import {IDateRange, IMessageProvider, IProgressProvider, MessageType} from '../PA.Core/PA.Core.IF.Basic';
import {PAdbContext} from '../PA.Core/PAdbContext';
import {IdentityProvider} from '../PA.Core/Basic/Identity'
import {MessageProvider} from '../PA.Core/Basic/Messages'
import {ProgressProvider} from '../PA.Core/Basic/Progress'
import {
    WB_CustomerRow,
    WB_CustomerTagRelationshipRow,
    WB_TagRow,
    WB_TagSourceRow,
    WB_CustomerRelationshipRow,
    WB_ProductGroupRow,
    WB_CustomerDistributionRelationshipRow,
    WB_CustomerSalesAreaRow,
    WB_SalesAreaRow,
    WB_CustomerRoleRelationshipRow,
    WB_CustomerRoleRow,
    WB_CustomerTypeRow,
    WB_MasterDataSetRow,
    WB_SourceRow,
    WB_SalesOrgRow,
    WB_DivisionRow,
    WB_DistribChannelRow,
} from "../PA.Core/PAdbSchemaRows";
import {IwbCustomer} from "../PA.Core/PAdbSchemaRowsIF";
import {
    esWB_Customer,
    esWB_Customer_Relationship,
    esWB_Customer_Distribution_Relationship,
    esWB_Product_Group,
    esWB_Customer_Tag_Relationship,
    esWB_Tag_Source,
    esWB_Tag,
    esWB_Customer_Role,
    esWB_Customer_Type,
    esWB_Customer_SalesArea_Relationship,
    esWB_Customer_Role_Relationship,
    esWB_SalesArea,
    esWB_MasterData_Set,
    esWB_Source,
    esWB_SalesOrg,
    esWB_Division,
    esWB_DistribChannel,
    esWB_Customer_Attribute
} from "../PA.Core/PAdbSchema";

import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {CreateOptions, ESRecordResolver} from "../PA.Core/lib/ESRecordResolver"
import {ASync} from "../PA.Core/Basic/ASync"
import {CustomerBOValidations} from "./CustomerBO.Validations"
import {CustomerHierarchyService} from "../PA.MD.Services/CustomerHierarchyService";
import {LeaseRegistry} from "../PA.Utils/LeaseRegistry";
import {CustomerBOAudit} from "./CustomerBO.Audit";
import {AuditService} from "../PA.Core/Security/AuditService";
import {IHaveTags} from "../PA.Core/PA.Core.IF.Aspects";
import {BehaviorSubject} from "rxjs";
import {PAEnvironment} from "../PA.Core/PAEnvironment";
import {DefaultChanges} from "../PA.Core/BusinessObject/BusinessObjectChanges";
import {CustomerHeaderFields, ICustomerType, IHaveCustomerType} from "../PA.MD.Core/PA.MD.IF.Customer";
import {CustomerEntity} from "../PA.MD.Entity/CustomerEntity";
import {IEntity} from "../PA.MD.Core/PA.MD.IF.Entity";
import {MDUsageService} from "../PA.MD.Services/MDUsageService";

export interface ICustomerCreateLoadOptions {
    // Load Options
    dbContext?: PAdbContext
    wbCustMId?: number

    // Create Options
    wbMasterDataSetId?: number
    sId?: string
    sKeySetId?: string
    sName?: string
    wbCustomerRoleId?: number
    wbSalesAreaId?: number | number[]
    wbSourceId?: number
    CUSTM_ID?: string
}

const CustomerLoadOptions_default: ICustomerCreateLoadOptions = {}

export class CustomerBO
    implements IBusinessObject,
        IHaveTags,
        IHaveCustomerType {

    //<editor-fold desc = "Properties and Constructor">

    Identity: IdentityProvider;

    CreateOptions: ICustomerCreateLoadOptions;

    LoadOptions: ICustomerCreateLoadOptions;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_Customer: WB_CustomerRow;

    dctAuditedDeletes: { [id: number]: number }

    _typeDef: WB_CustomerTypeRow;

    constructor(options?: ICustomerCreateLoadOptions) {

        this.CreateOptions = options || {};

        if ('dbContext' in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({id: 0, name: 'New Customer'});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = undefined;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this.drWB_Customer = undefined;
        this._typeDef = undefined

        this.dctAuditedDeletes = {}
        this.IHaveTagsLoaded_init()
        this.IHaveSalesAreas_init()
        this.IHaveCustomerRoles_init()
        this.IHaveCustomerType_init()
        this.Entity_init()
    }

    //</editor-fold>

    get WB_Customer(): WB_CustomerRow {
        return this.drWB_Customer;
    }


    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: ICustomerCreateLoadOptions): Promise<IBusinessObject> {
        const self = this;
        this.LoadProgressProvider.start("Creating Customer");

        let internalOnComplete = self._OnLoadComplete = (async () => {

            try {
                self.LoadOptions = options || {};
                if ('dbContext' in self.LoadOptions)
                    self.dbContext = self.LoadOptions.dbContext;
                else
                    self.dbContext = new PAdbContext();

                if (this.dbContext == null) {
                    let sError = "Unable to determine dbContext for new Customer.";
                    self.LoadProgressProvider.exception(sError);
                    return self;
                }

                // Create record using dbContext
                let newCustomerRow: WB_CustomerRow = <WB_CustomerRow>await self.dbContext.createRecordWithKey(esWB_Customer)

                newCustomerRow.setDefaults()
                self.drWB_Customer = newCustomerRow;
                self.dbContext.add(esWB_Customer, newCustomerRow)
                self.Identity.setAll(self.WB_Customer.cif.WB_CUST_ID, self.WB_Customer.cif.CUSTOMER_ID, self.WB_Customer.cif.CUSTOMER_NAME);
                self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                self.Identity.listenToRecordUpdates(self.WB_Customer, 'WB_CUST_ID', 'CUSTOMER_ID', 'CUSTOMER_NAME')


                self._bIsLoaded = true;
                self._OnLoadComplete = null;

                await self.OnIHaveTagsLoaded

                // Create Defaults
                await self.applyCreateOptions()
                self.Identity.setAll(self.WB_Customer.cif.WB_CUST_ID, self.WB_Customer.cif.CUSTOMER_ID, self.WB_Customer.cif.CUSTOMER_NAME);

                await self.IHaveCustomerType_OnLoadComplete()

                await self.Entity_OnLoadComplete()

                self.LoadProgressProvider.finish();
                return self
            } catch (err) {
                self.LoadProgressProvider.exception(err)
                return self
            }

        })();

        return internalOnComplete
    }

    async applyCreateOptions() {
        const self = this

        let sId = self.CreateOptions?.sId || self.LoadOptions?.sId
        if (!_.isEmpty(sId))
            self.WB_Customer.cif.CUSTOMER_ID = sId

        let sName = self.CreateOptions?.sName || self.LoadOptions?.sName
        if (!_.isEmpty(sName))
            self.WB_Customer.cif.CUSTOMER_NAME = sName


        let sKeySetId = self.CreateOptions?.sKeySetId || self.LoadOptions?.sKeySetId
        if (!_.isEmpty(sKeySetId))
            self.WB_Customer.cif.CUSTOMER_SET_ID = sKeySetId

        // Set Master Data Set
        let wbMasterDataSet = self.CreateOptions?.wbMasterDataSetId || self.LoadOptions?.wbMasterDataSetId
        if (_.isNumber(wbMasterDataSet)) {
            let mds: WB_MasterDataSetRow = <any>await self.dbContext.find(esWB_MasterData_Set, wbMasterDataSet)
            if (mds) {
                let mruValue= new MDUsageService()
                self.WB_Customer.WB_MasterDataSetRow = mds
                self.WB_Customer.cif.WB_MDSET_ID = wbMasterDataSet
                await mruValue.saveMDSetUsage(wbMasterDataSet)

            }
        }
        // Set Source
        let wbSource = self.CreateOptions?.wbSourceId || self.LoadOptions?.wbSourceId
        if (_.isNumber(wbSource)) {
            let src: WB_SourceRow = <any>await self.dbContext.find(esWB_Source, wbSource)
            if (src) {
                self.WB_Customer.WB_SourceRow = src
                self.WB_Customer.cif.WB_SOURCE_ID = wbSource
                self.WB_Customer.cif.SOURCE_ID = src.cif.SOURCE_ID
            }
        }

        // Add SalesArea
        let wbSalesAreaId = self.CreateOptions?.wbSalesAreaId || self.LoadOptions?.wbSalesAreaId
        if (_.isNumber(wbSalesAreaId) && !_.isArray(wbSalesAreaId))
            wbSalesAreaId = [wbSalesAreaId]
        for (let aSalesAreaId of (wbSalesAreaId || [])) {
            let newRec = await self.addSalesArea(aSalesAreaId)
        }

        let wbCustRoleId = self.CreateOptions?.wbCustomerRoleId || self.LoadOptions?.wbCustomerRoleId
        let dctCustRoleId: {[id:number]: number} = {}
        if (_.isNumber(wbCustRoleId))
            dctCustRoleId[wbCustRoleId] = wbCustRoleId

        let wbCustMId = self.CreateOptions?.wbCustMId || self.LoadOptions?.wbCustMId
        if(_.isNumber(wbCustMId)){
            let wbCustRoleId: number = 0
            let arrCustSrc = <WB_CustomerRoleRow[]> await self.dbContext.findAll(esWB_Customer_Role, {
                where: {
                    WB_CUSTM_ID: wbCustMId,
                    DELETED_IND: {neq: '1'}
                }
            })
            _.forEach(arrCustSrc, (cr) => {
                if(!wbCustRoleId)
                    wbCustRoleId = cr.cif.WB_CUSTROLE_ID
                if(cr.cif.DEFAULT_IND = '1')
                    wbCustRoleId = cr.cif.WB_CUSTROLE_ID
            })
            if(wbCustRoleId > 0)
                dctCustRoleId[wbCustRoleId] = wbCustRoleId
        }


        for(let wbCustomerRoleId of _.values(dctCustRoleId)){
            for (let aSalesAreaId of (wbSalesAreaId || [])) {
                let newRec = await self.addCustomerRole(wbCustomerRoleId, aSalesAreaId)
                self._typeDef = newRec?.WB_CustomerRoleRow?.WB_CustomerTypeRow
            }
        }

    }


    Load(anId: number | number, options?: ICustomerCreateLoadOptions): Promise<IBusinessObject> {

        const self = this;

        let recordResolver: ESRecordResolver
        let recordResolverLoadPromise: Promise<void>

        self.LoadProgressProvider.start("Loading (" + anId + ")");

        let internalOnComplete = self._OnLoadComplete = (async () => {

            try {
                if (self.bIsLoaded) {
                    self.LoadProgressProvider.exception("Customer already loaded.")
                    return self;
                }

                self.LoadOptions = options || {};
                _.defaults(self.LoadOptions, CustomerLoadOptions_default)

                if ('dbContext' in self.LoadOptions)
                    self.dbContext = self.LoadOptions.dbContext;
                else if (_.isEmpty(self.dbContext)) {
                    self.dbContext = new PAdbContext({});
                }

                if (_.isEmpty(self.dbContext))
                    throw "Unable to determine dbContext for Customer."

                if ('RecordResolver' in self.LoadOptions) {
                    recordResolver = (<any>self.LoadOptions).RecordResolver
                    recordResolverLoadPromise = ASync.resolve()
                } else {
                    recordResolver = CustomerBO.createRecordResolver({dbContext: self.dbContext})
                    recordResolverLoadPromise = CustomerBO.setupRecordResolver(recordResolver, [anId])
                }


                await recordResolverLoadPromise

                let aCustomerRow: any = recordResolver.get(esWB_Customer, anId)

                if (_.isEmpty(aCustomerRow))
                    throw "Customer not Found"


                self.drWB_Customer = aCustomerRow;
                self.Identity.setAll(self.WB_Customer.cif.WB_CUST_ID, self.WB_Customer.cif.CUSTOMER_ID, self.WB_Customer.cif.CUSTOMER_NAME);
                self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                self.Identity.listenToRecordUpdates(self.WB_Customer, 'WB_CUST_ID', 'CUSTOMER_ID', 'CUSTOMER_NAME')

                let mruValue= new MDUsageService()
                await mruValue.saveMDSetUsage(self.drWB_Customer.cif.WB_MDSET_ID)

                self._bIsLoaded = true
                await Promise.all([self.OnIHaveTagsLoaded, self.IHaveCustomerType_OnLoadComplete()])
                await self.Entity_OnLoadComplete()

                self.LoadProgressProvider.finish()

            } catch (err) {
                self.LoadProgressProvider.exception(err)

            } finally {
                self._OnLoadComplete = undefined
                return self
            }
        })();


        return internalOnComplete;
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    Validate(options?: any): Promise<IBusinessObject> {

        var self = this;
        this.ValidateMessageProvider.clearMessages();
        this.ValidateProgressProvider.start("Validating " + self.WB_Customer.cif.CUSTOMER_NAME + "(" + self.WB_Customer.cif.CUSTOMER_ID + ")");

        return (async () => {
            // Does no validations yet
            self.ValidateOptions = options || {};

            try {

                if (self.dbContext == undefined)
                    throw "Unable to determine dbContext for to Validate Customer.";


                await CustomerBOValidations.Validation(self)
                if(self.customerEntity){
                    await self.customerEntity.Validate()
                    self.ValidateMessageProvider.addMessages(self.customerEntity.ValidateMessageProvider.Messages)
                }

                self._bIsValidated = (self.ValidateMessageProvider.countMessages(MessageType.Error) == 0);
                self.ValidateProgressProvider.finish();

            } catch (err) {
                self._bIsValidated = false;
                self.ValidateProgressProvider.exception(err);
            } finally {
                return self
            }

        })();

    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }


    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options)
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options)
    }

    async SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {

        const self = this;

        const wbCust = <IwbCustomer>self.drWB_Customer;
        let auditService: AuditService = undefined

        try {
            self.SaveProgressProvider.start("Saving " + wbCust.CUSTOMER_NAME + "(" + wbCust.CUSTOMER_ID + ")");

            // Doesn't need to do anything yet
            // All the records are in the store, since they were added through find or createNewRecord.
            // -- We could search the tree if the there is any record where store isn't set.

            if (self.dbContext == undefined)
                throw  "Unable to determine dbContext to Save Customer.";

            self.SaveOptions = options || {};

            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext)
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext)
            }

            await self.Validate({})

            if (!self.bIsValidated) {
                self._bIsSaved = false;
                self.SaveProgressProvider.finish();
                return self
            }

            auditService = await CustomerBOAudit.CustomerAuditBO(self)

            //-- Invalidate Hierarchy Leases
            let dctLeaseClasses: { [id: string]: string } = {};
            let propsToCheck: string[] = ["WB_CUSTH_ID", "WB_CUST_ID", "WB_CUST_ID_PRNT", "EFFECTIVE_START_DT", "EFFECTIVE_END_DT", "DELETED_IND"];
            let leaseClasses: string[];
            let custHWbID: number;
            let allRelRows: any[] = [];
            allRelRows.push.apply(allRelRows, self.drWB_Customer.WB_CustomerRelationshipRows_Children);
            allRelRows.push.apply(allRelRows, self.drWB_Customer.WB_CustomerRelationshipRows_Parents);

            _.forEach(allRelRows, (aRel: ESRecord) => {
                if (!aRel._rowExists || (aRel._rowExists && aRel._rowDeleted) || aRel.hasChanges({properties: propsToCheck})) {
                    custHWbID = (aRel as WB_CustomerRelationshipRow).cif.WB_CUSTH_ID;

                    leaseClasses = CustomerHierarchyService.getLeaseClasses(custHWbID);
                    dctLeaseClasses = _.keyBy(leaseClasses, (aVal) => {
                        return aVal
                    });
                }
            });

            await saveFunction(esWB_Customer, self.WB_Customer, {})

            let tasks: Promise<any>[] = []

            tasks.push(saveFunction(esWB_Customer_Relationship, self.WB_Customer.WB_CustomerRelationshipRows_Children))
            tasks.push(saveFunction(esWB_Customer_Relationship, self.WB_Customer.WB_CustomerRelationshipRows_Parents))
            tasks.push(saveFunction(esWB_Customer_Distribution_Relationship, self.WB_Customer.WB_CustomerDistributionRelationshipRows))
            tasks.push(saveFunction(esWB_Customer_Tag_Relationship, self.WB_Customer.WB_CustomerTagRelationshipRows))
            tasks.push(saveFunction(esWB_Customer_Role_Relationship, self.WB_Customer.WB_CustomerRoleRelationshipRows))
            tasks.push(saveFunction(esWB_Customer_SalesArea_Relationship, self.WB_Customer.WB_CustomerSalesAreaRows))
            tasks.push(saveFunction(esWB_Customer_Attribute, self.WB_Customer.WB_CustomerAttributeRows))
            await Promise.all(tasks)

            dctLeaseClasses[LeaseRegistry.Lease_Type_Customer_Hierarchy] = LeaseRegistry.Lease_Subject_Customer_Hierarchy
            dctLeaseClasses[LeaseRegistry.Lease_Type_ReportingDimensionHierarchy] = LeaseRegistry.Lease_Type_ReportingDimensionHierarchy
            if (bBatch) {
                self.dbContext.addBatchCompleteListener(() => {
                    self._bIsSaved = true;
                    if (!_.isEmpty(_.values(dctLeaseClasses))) {
                        LeaseRegistry.generateLeaseInvalidationByClassList(_.values(dctLeaseClasses));
                    }
                })
            } else {
                self._bIsSaved = true;
                if (!_.isEmpty(_.values(dctLeaseClasses))) {
                    LeaseRegistry.generateLeaseInvalidationByClassList(_.values(dctLeaseClasses));
                }
            }
            self.SaveProgressProvider.finish();
        } catch (err) {
            self.SaveProgressProvider.exception(err);

        } finally {
            if (auditService)
                await auditService.Save(bBatch)
        }
    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

//Delete
    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options)
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options)
    }

    private async DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        const wbCustomer = <IwbCustomer>self.drWB_Customer;

        try {
            self.DeleteMessageProvider.clearMessages();
            self.DeleteProgressProvider.start("Deleting " + wbCustomer.CUSTOMER_NAME + "(" + wbCustomer.WB_CUST_ID + ")");

            // 1. Validate if we can delete the Customer
            // 2. If Valid, mark all the records for deletion.

            //TODO: Add more extensive checks

            if (!self.WB_Customer.bManual) {
                let msg = 'Cannot delete Interfaced Customer'
                self.DeleteMessageProvider.addMessage({
                    code: 'InterfacedCustomer',
                    description: msg,
                    title: msg,
                    type: MessageType.Error
                })
            }

            if (self.DeleteMessageProvider.countMessages(MessageType.Error) > 0) {
                // Don't delete..
                this.DeleteProgressProvider.finish();
                return;
            }

            self.drWB_Customer.markDeleted()

            _.forEach(self.drWB_Customer.WB_CustomerDistributionRelationshipRows, (a) => a.markDeleted())
            _.forEach(self.drWB_Customer.WB_CustomerRelationshipRows_Children, (a) => a.markDeleted())
            _.forEach(self.drWB_Customer.WB_CustomerRelationshipRows_Parents, (a) => a.markDeleted())
            _.forEach(self.drWB_Customer.WB_CustomerTagRelationshipRows, (a) => a.markDeleted())
            _.forEach(self.drWB_Customer.WB_CustomerSalesAreaRows, (a) => a.markDeleted())
            _.forEach(self.drWB_Customer.WB_CustomerRoleRelationshipRows, (a) => a.markDeleted())

            if (bSaveNow)
                await self.SaveUsing(false)

            self._bIsDeleted = true;
            self.DeleteProgressProvider.finish();
        } catch (err) {
            this.DeleteProgressProvider.exception(err)
        } finally {
            return self
        }
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    Changes(options?: IBusinessObjectChangesOptions): IBusinessObjectChanges {
        let opts = options || {}
        // Set any BO Specific change settings
        return DefaultChanges(this, this.drWB_Customer, opts)
    }

//</editor-fold>


    //<editor-fold desc = "Customer Relationship operations">

    addHierarchy(aWbCustId: number, sType: string, aWbCustHId: number): Promise<CustomerBO> {

        const self = this

        return new Promise<CustomerBO>((resolve, error) => {

            // first find out if record exists.
            var found = false

            if (sType === 'Child')
                _.forEach(self.drWB_Customer.WB_CustomerRelationshipRows_Children, (aCustChildRelRow) => {
                    if (aCustChildRelRow.isActive && aCustChildRelRow.cif.WB_PRDGRP_ID == aWbCustId) {
                        found = true
                        return false
                    }
                })
            else
                _.forEach(self.drWB_Customer.WB_CustomerRelationshipRows_Parents, (aCustParentRelRow) => {
                    if (aCustParentRelRow.isActive && aCustParentRelRow.cif.WB_PRDGRP_ID_PRNT == aWbCustId) {
                        found = true
                        return false
                    }
                })

            if (found) {
                return
            } // exists..
            var aCustRow: WB_CustomerRow = undefined

            self.dbContext.find(esWB_Customer, aWbCustId)
                .then((aCustRecord: WB_CustomerRow) => {
                    if (_.isEmpty(aCustRecord)) {
                        resolve(self)
                        return
                    }
                    aCustRow = aCustRecord

                    // create new records
                    return self.dbContext.createRecordWithKey(esWB_Customer_Relationship)

                })
                .then((aCustRelRow: WB_CustomerRelationshipRow) => {
                    aCustRelRow.setDefaults()
                    if (sType === 'Child') {
                        aCustRelRow.cif.WB_CUST_ID = aWbCustId
                        aCustRelRow.WB_CustomerRowChild = aCustRow
                        aCustRelRow.cif.WB_CUST_ID_PRNT = self.drWB_Customer.cif.WB_CUST_ID
                        aCustRelRow.cif.MANUAL_IND = '1'
                    } else {
                        aCustRelRow.cif.WB_CUST_ID_PRNT = aWbCustId
                        aCustRelRow.WB_CustomerRowParent = aCustRow
                        aCustRelRow.cif.WB_CUST_ID = self.drWB_Customer.cif.WB_CUST_ID
                        aCustRelRow.cif.MANUAL_IND = '1'
                    }
                    self.dbContext.add(esWB_Customer_Relationship, aCustRelRow)
                    resolve(self)
                })
                .catch((err) => {
                    error(err)
                })
        })

    }


    removeHierarchy(aWbCustId: number, sType: string): Promise<CustomerBO> {
        var self = this

        return new Promise<CustomerBO>((resolve, error) => {

            if (sType === 'Child')
                _.forEach(self.drWB_Customer.WB_CustomerRelationshipRows_Children, (aCustRelRow: WB_CustomerRelationshipRow) => {
                    if (aCustRelRow.isActive && aCustRelRow.cif.WB_CUSTR_ID == aWbCustId) {
                        aCustRelRow.markDeleted()
                    }
                })
            else
                _.forEach(self.drWB_Customer.WB_CustomerRelationshipRows_Parents, (aCustRelRow: WB_CustomerRelationshipRow) => {
                    if (aCustRelRow.isActive && aCustRelRow.cif.WB_CUSTR_ID == aWbCustId) {
                        aCustRelRow.markDeleted()
                    }
                })

            resolve(self)
        })
    }

    //</editor-fold">


    //<editor-fold desc = "Wholesaler">

    addWholeSaler(aWbCustrpgId: number, aWBProductGroupId: number, options: any, callback?: any): Promise<IBusinessObject> {
        const self = this

        return new Promise<IBusinessObject>((resolve, error) => {
            var aCustomerRow: WB_CustomerRow = undefined

            var aProductGroupRow: WB_ProductGroupRow = undefined
            //Is the Customer in the customer table?
            self.dbContext.find(esWB_Customer, aWbCustrpgId)
                .then((aCustomerRecord: WB_CustomerRow) => {
                    if (_.isEmpty(aCustomerRecord)) {
                        // Bad Whole Saler ID
                        resolve(self)
                        return
                    }

                    aCustomerRow = aCustomerRecord
                    return self.dbContext.find(esWB_Product_Group, aWBProductGroupId)
                })
                .then((productGroupRow: WB_ProductGroupRow) => {
                    if (_.isEmpty(productGroupRow)) {
                        // Bad Product Group ID
                        resolve(self)
                        return
                    }
                    aProductGroupRow = productGroupRow
                    // create new records
                    return self.dbContext.createRecordWithKey(esWB_Customer_Distribution_Relationship)
                })
                .then((aCustDisRelRow: WB_CustomerDistributionRelationshipRow) => {
                    aCustDisRelRow.setDefaults()
                    aCustDisRelRow.cif.WB_CUST_ID = self.drWB_Customer.cif.WB_CUST_ID
                    aCustDisRelRow.cif.WB_PRDGRP_ID = aProductGroupRow.cif.WB_PRDGRP_ID
                    aCustDisRelRow.WB_ProductGroupRow = aProductGroupRow
                    aCustDisRelRow.cif.WB_CUST_ID_REL = aCustomerRow.cif.WB_CUST_ID

                    aCustDisRelRow.WB_CustomerRow_Related = aCustomerRow
                    return self.dbContext.add(esWB_Customer_Distribution_Relationship, aCustDisRelRow);
                }).then(res => {
                console.log(res, 'res')
                callback()
                resolve(self)
            })
                .catch((err) => {
                    error(err)
                })
        })
    }

    removeWholeSalerRow(aWbCustrpgId: number, options: any): Promise<CustomerBO> {
        const self = this

        return new Promise<CustomerBO>((resolve, error) => {

            _.forEach(self.drWB_Customer.WB_CustomerDistributionRelationshipRows, (aCustDisRelRow: WB_CustomerDistributionRelationshipRow) => {
                if (aCustDisRelRow.isActive && aCustDisRelRow.cif.WB_CUSTRPG_ID == aWbCustrpgId) {
                    aCustDisRelRow.markDeleted()
                }
            })

            resolve(self)
        })
    }

    //</editor-fold>

    //<editor-fold desc = "Sales Area">

    _salesAreasChanged: BehaviorSubject<CustomerBO>

    IHaveSalesAreas_init() {
        this._salesAreasChanged = new BehaviorSubject<CustomerBO>(this)
    }

    get SalesAreasChanged(): BehaviorSubject<CustomerBO> {
        return this._salesAreasChanged
    }

    async addSalesArea(wbSalesAreaId: number): Promise<WB_CustomerSalesAreaRow> {
        const self = this

        for (let rel of self.WB_Customer.WB_CustomerSalesAreaRows) {
            if (!rel.isActive)
                continue
            if (rel.cif.WB_SALESAREA_ID == wbSalesAreaId)
                return rel
        }

        let saRow: WB_SalesAreaRow = await self.dbContext.find(esWB_SalesArea, wbSalesAreaId)
        if (_.isEmpty(saRow))
            return undefined

        if (saRow.cif.WB_SALESORG_ID > 0) {
            let soRow: WB_SalesOrgRow = <any>await self.dbContext.find(esWB_SalesOrg, saRow.cif.WB_SALESORG_ID)
            if (!_.isEmpty(soRow) && _.isEmpty(saRow.WB_SalesOrgRow)) {
                saRow.WB_SalesOrgRow = soRow
            }
        }

        if (saRow.cif.WB_DIVISION_ID > 0) {
            let dvRow: WB_DivisionRow = <any>await self.dbContext.find(esWB_Division, saRow.cif.WB_DIVISION_ID)
            if (!_.isEmpty(dvRow) && _.isEmpty(saRow.WB_DivisionRow)) {
                saRow.WB_DivisionRow = dvRow
            }
        }

        if (saRow.cif.WB_DISTRIBCHANNEL_ID > 0) {
            let dcRow: WB_DistribChannelRow = <any>await self.dbContext.find(esWB_DistribChannel, saRow.cif.WB_DISTRIBCHANNEL_ID)
            if (!_.isEmpty(dcRow) && _.isEmpty(saRow.WB_DistribChannelRow)) {
                saRow.WB_DistribChannelRow = dcRow
            }
        }

        let newRow: WB_CustomerSalesAreaRow = <any>await self.dbContext.createRecordWithKey(esWB_Customer_SalesArea_Relationship)
        newRow.setDefaults()
        newRow.WB_CustomerRow = self.WB_Customer
        newRow.cif.WB_CUST_ID = self.WB_Customer.cif.WB_CUST_ID
        newRow.WB_SalesAreaRow = saRow
        newRow.cif.WB_SALESAREA_ID = wbSalesAreaId

        self.SalesAreasChanged.next(self)
        return newRow
    }

    async removeSalesArea(wbCustSRId: number): Promise<void> {
        const self = this

        for (let rel of self.WB_Customer.WB_CustomerSalesAreaRows) {
            if (!rel.isActive)
                continue
            if (rel.cif.WB_CUSTSR_ID == wbCustSRId) {
                rel.markDeleted()
                break
            }
        }

        self.SalesAreasChanged.next(self)
    }


    //</editor-fold>

    //<editor-fold desc = "Roles by Sales Area">

    _customerRolesChanged: BehaviorSubject<CustomerBO>

    IHaveCustomerRoles_init() {
        this._customerRolesChanged = new BehaviorSubject<CustomerBO>(this)
    }

    get CustomerRolesChanged(): BehaviorSubject<CustomerBO> {
        return this._customerRolesChanged
    }

    async addCustomerRole(wbCustomerRoleId: number, wbSalesAreaId: number): Promise<WB_CustomerRoleRelationshipRow> {
        const self = this

        for (let rel of self.WB_Customer.WB_CustomerRoleRelationshipRows) {
            if (!rel.isActive)
                continue
            if ((rel.cif.WB_SALESAREA_ID == wbSalesAreaId) && (rel.cif.WB_CUSTROLE_ID == wbCustomerRoleId))
                return rel
        }

        let saRow: WB_SalesAreaRow = await self.dbContext.find(esWB_SalesArea, wbSalesAreaId)
        if (_.isEmpty(saRow))
            return undefined

        let crRow: WB_CustomerRoleRow = await self.dbContext.find(esWB_Customer_Role, wbCustomerRoleId)
        if (_.isEmpty(crRow))
            return undefined

        let ctRow: WB_CustomerTypeRow = undefined
        if (crRow.cif.WB_CUSTM_ID > 0) {
            ctRow = await self.dbContext.find(esWB_Customer_Type, crRow.cif.WB_CUSTM_ID)
            if (_.isEmpty(crRow.WB_CustomerTypeRow))
                crRow.WB_CustomerTypeRow = ctRow
        }
        let newRow: WB_CustomerRoleRelationshipRow = <any>await self.dbContext.createRecordWithKey(esWB_Customer_Role_Relationship)
        newRow.setDefaults()
        newRow.WB_CustomerRow = self.WB_Customer
        newRow.cif.WB_CUST_ID = self.WB_Customer.cif.WB_CUST_ID
        newRow.WB_SalesAreaRow = saRow
        newRow.cif.WB_SALESAREA_ID = wbSalesAreaId
        newRow.WB_CustomerRoleRow = crRow
        newRow.cif.WB_CUSTROLE_ID = wbCustomerRoleId

        self.CustomerRolesChanged.next(self)
        return newRow
    }

    async removeCustomerRole(wbCustRSRId: number): Promise<void> {
        const self = this

        for (let rel of self.WB_Customer.WB_CustomerRoleRelationshipRows) {
            if (!rel.isActive)
                continue
            if (rel.cif.WB_CUSTRSR_ID == wbCustRSRId) {
                rel.markDeleted()
                break
            }
        }

        self.CustomerRolesChanged.next(self)
    }


    //</editor-fold>

    //<editor-fold desc = "CustomerType">
    private _CustomerTypeChanged: BehaviorSubject<IHaveCustomerType>
    private _CustomerType: ICustomerType
    private _CustomerTypeAll: ICustomerType[]
    private _dctCustomerType: { [id: number]: ICustomerType }

    private IHaveCustomerType_init() {
        this._CustomerTypeChanged = new BehaviorSubject<IHaveCustomerType>(this)
        this._CustomerType = undefined
        this._CustomerTypeAll = []
        this._dctCustomerType = {}
    }

    private async IHaveCustomerType_OnLoadComplete() {
        this.IHaveCustomerType_refresh(true);
    }


    private IHaveCustomerType_refresh(bSetDefault?: boolean) {
        const self = this

        let curCT = self._CustomerType

        // Get the distinct list of Customer Types
        let dctCustTypes: { [id: number]: WB_CustomerTypeRow } = {}
        _.forEach(self.WB_Customer.WB_CustomerRoleRelationshipRows, (a) => {
            if (!a.isActive)
                return
            if (a.WB_CustomerRoleRow?.WB_CustomerTypeRow) {
                let ct = a.WB_CustomerRoleRow.WB_CustomerTypeRow
                dctCustTypes[ct.cif.WB_CUSTM_ID] = ct
            }

        })

        if (_.isEmpty(dctCustTypes)) {
            let ct = self.createICustomerType(undefined)
            self._CustomerType = ct
            self._CustomerTypeAll = [ct]
            self._dctCustomerType = {[ct.id]: ct}
        } else {
            self._CustomerTypeAll = []
            self._dctCustomerType = {}
            _.forEach(dctCustTypes, (a) => {
                let ct = self.createICustomerType(a)
                self._dctCustomerType[ct.id] = ct
                self._CustomerTypeAll.push(ct)
            })
        }

        if (bSetDefault || _.isEmpty(curCT)) {
            // Check the Create Options
            let wbCustMId: number = 0
            if (self.CreateOptions?.wbCustMId > 0)
                wbCustMId = self.CreateOptions?.wbCustMId
            else if (self.LoadOptions?.wbCustMId > 0)
                wbCustMId = self.LoadOptions?.wbCustMId
            else if (!_.isEmpty(self.LoadOptions?.CUSTM_ID)) {
                wbCustMId = _.first(_.filter(self._CustomerTypeAll, aType => aType.id_ui == self.LoadOptions?.CUSTM_ID)).id
            } else if (self.LoadOptions?.wbCustomerRoleId > 0) {
                wbCustMId = self.drWB_Customer.WB_CustomerRoleRelationshipRows[0]?.WB_CustomerRoleRow?.cif?.WB_CUSTM_ID
            }


            if (wbCustMId in self._dctCustomerType)
                self._CustomerType = self._dctCustomerType[wbCustMId]
            else
                self._CustomerType = _.first(self._CustomerTypeAll)
        }

        // Check if Customer Type is invalid, if so, reset to first
        if (_.isEmpty(self._CustomerType) || (!(self._CustomerType.id in self._dctCustomerType))) {
            self._CustomerType = _.first(self._CustomerTypeAll)
        }
    }

    private createICustomerType(ct: WB_CustomerTypeRow): ICustomerType {
        if (_.isEmpty(ct)) {
            let rv: ICustomerType = {
                id: 0,
                id_ui: "NA",
                name: "No Assigned Role",
                bAddressSearchByInd: true,
                bAddressShowInd: true,
                bSalesAreaSearchByInd: true,
                bSalesAreaShowInd: true,
                bStatusSearchByInd: true,
                bStatusShowInd: true,
                CustomerFieldsShowInd: {},
                SalesAreaFieldsShowInd: {}
            }

            _.forEach(CustomerHeaderFields, (fieldName) => {
                rv.CustomerFieldsShowInd[fieldName] = true
            })

            return rv
        }
        let rv: ICustomerType = {
            id: ct.cif.WB_CUSTM_ID,
            id_ui: ct.cif.CUSTM_ID,
            name: ct.cif.CUSTM_NAME,
            bAddressSearchByInd: ct.bAddressSearchByInd,
            bAddressShowInd: ct.bAddressShowInd,
            bSalesAreaSearchByInd: ct.bSalesAreaSearchByInd,
            bSalesAreaShowInd: ct.bSalesAreaShowInd,
            bStatusSearchByInd: ct.bStatusSearchByInd,
            bStatusShowInd: ct.bStatusShowInd,
            CustomerFieldsShowInd: {},
            SalesAreaFieldsShowInd: {}
        }

        let dctHeaderShowFields = _.keyBy(ct.cif.HEADER_SHOW_FIELDS.split(","), (a) => a)
        _.forEach(CustomerHeaderFields, (fieldName) => {
            rv.CustomerFieldsShowInd[fieldName] = (fieldName in dctHeaderShowFields)
        })

        return rv
    }

    get CustomerType(): ICustomerType {
        return this._CustomerType
    }

    get CustomerTypesAll(): ICustomerType[] {
        return this._CustomerTypeAll
    }

    get CustomerTypeChanged(): BehaviorSubject<IHaveCustomerType> {
        return this._CustomerTypeChanged
    }

    setCustomerType(cr: ICustomerType): void {
        let id = cr?.id
        if (id in this._dctCustomerType) {
            this._CustomerType = this._dctCustomerType[id]
        }
        this.CustomerTypeChanged.next(this)
    }


    //</editor-fold>

    //<editor-fold desc = "Entity - Attributes">

    private customerEntity: CustomerEntity

    Entity_init(): void {

    }

    async Entity_OnLoadComplete(): Promise<void> {
        this.customerEntity = new CustomerEntity(this.drWB_Customer)
        await this.customerEntity.OnLoadComplete()
    }

    get Entity(): IEntity {
        return this.customerEntity
    }

    //</editor-fold>

    //<editor-fold desc = "IHaveTags">

    _bIHaveTagsLoaded: boolean;
    _OnIHaveTagsLoaded: Promise<IHaveTags>;
    _TagsChanged: BehaviorSubject<IHaveTags>;
    _AllTags: WB_TagRow[];
    _TagUsageId: string;


    IHaveTagsLoaded_init() {
        this._bIHaveTagsLoaded = false
        this._OnIHaveTagsLoaded = undefined
        this._TagsChanged = new BehaviorSubject<IHaveTags>(this)
        this._AllTags = undefined
        this._TagUsageId = 'AllTagUsage'
    }

    get bIHaveTagsLoaded() {
        return this._bIHaveTagsLoaded
    }

    get TagsReadOnly(): boolean {
        if (this._bIsDeleted) {
            return true
        }
        // Check User Roles ?

        return false
    }

    get Tags(): WB_TagRow[] {
        var returnValue: WB_TagRow[] = []

        _.forEach(this.drWB_Customer.WB_CustomerTagRelationshipRows, (aWBCustomerTagRow: WB_CustomerTagRelationshipRow) => {
            if (aWBCustomerTagRow && aWBCustomerTagRow.isActive && aWBCustomerTagRow.WB_TagRow) {
                returnValue.push(aWBCustomerTagRow.WB_TagRow)
            }
        })

        return returnValue
    }

    get TagsChanged(): BehaviorSubject<IHaveTags> {
        return this._TagsChanged
    }


    get AllTags(): WB_TagRow[] {
        if (this._AllTags) {
            return this._AllTags
        } else {
            return []
        }
    }

    get UsageId(): string {
        return this._TagUsageId
    }

    get OnIHaveTagsLoaded(): Promise<IHaveTags> {
        const self = this;

        if (self._bIHaveTagsLoaded) {
            return ASync.resolve(self)
        }

        if (!_.isEmpty(self._OnIHaveTagsLoaded)) {
            return self._OnIHaveTagsLoaded;
        }

        self._OnIHaveTagsLoaded = new Promise<IHaveTags>((resolve, error) => {
            // If the BO is not loaded, throw an error.. It should have been started
            if ((!(self.bIsLoaded)) && (_.isEmpty(self.OnLoadComplete))) {
                error("Customer isn't Loaded or Loading.. Can't determine Tags");
                return;
            }

            var loadWait: Promise<any> = undefined
            if (self.bIsLoaded) {
                loadWait = ASync.resolve(self)
            } else {
                loadWait = self.OnLoadComplete
            }

            loadWait
                .then((aCustomerBO) => {
                    if (!(aCustomerBO.bIsLoaded)) {
                        error("Waited on Customer Load to complete but still isn't Loaded");
                        return
                    }

                    return self.dbContext.findAll(esWB_Tag_Source, {TAG_SOURCE_ID: 'CUSTOMER'})
                })
                .then((sourceRows: WB_TagSourceRow[]) => {
                    let source_id = 0
                    if (sourceRows) {
                        source_id = sourceRows[0].cif.WB_TAG_SOURCE_ID
                    }
                    return self.dbContext.findAll(esWB_Tag, {WB_TAG_SOURCE_ID: source_id})
                })
                .then((arrResultRows: WB_TagRow[]) => {
                    if (!_.isEmpty(arrResultRows)) {
                        self._AllTags = arrResultRows
                    } else {
                        self._AllTags = []
                    }

                    self._bIHaveTagsLoaded = true
                    self._OnIHaveTagsLoaded = undefined
                    resolve(self);
                    return
                })
                .catch((err) => {
                    self._OnIHaveTagsLoaded = undefined
                    error(err)
                });
        })

        return self._OnIHaveTagsLoaded;
    }

    addTag(TagId: number): Promise<void> {
        const self = this
        return new Promise<void>((resolve, error) => {
            var aTagRow: WB_TagRow = undefined
            self.dbContext.find(esWB_Tag, TagId)
                .then((aNewRow: WB_TagRow) => {
                    if (!(aNewRow)) {
                        error("Unable to find Tag to add to Customer. " + TagId)
                        return
                    }

                    aTagRow = aNewRow

                    return self.dbContext.createRecordWithKey(esWB_Customer_Tag_Relationship)

                })
                .then((aWBCustomerTag: WB_CustomerTagRelationshipRow) => {
                    //
                    if (!(aWBCustomerTag)) {
                        error("Unable to create Event Tag record add to Customer. ")
                        return
                    }
                    aWBCustomerTag.setDefaults()
                    aWBCustomerTag.cif.WB_TAG_ID = aTagRow.cif.WB_TAG_ID
                    aWBCustomerTag.WB_TagRow = aTagRow
                    aWBCustomerTag.cif.WB_CUST_ID = self.drWB_Customer.cif.WB_CUST_ID
                    aWBCustomerTag.cif.TAG_METHOD = "Manual"
                    self.dbContext.add(esWB_Customer_Tag_Relationship, aWBCustomerTag)
                    self.TagsChanged.next(self)
                    resolve()
                })
                .catch((err) => {
                    error(err)
                })
        })

    }

    removeTag(TagId: number): void {
        const self = this
        var changed = false
        _.forEach(this.drWB_Customer.WB_CustomerTagRelationshipRows, (aWBCustomerTagRow: WB_CustomerTagRelationshipRow) => {
            if (aWBCustomerTagRow && aWBCustomerTagRow.isActive && aWBCustomerTagRow.WB_TagRow) {
                if (aWBCustomerTagRow.WB_TagRow.cif.WB_TAG_ID == TagId) {
                    aWBCustomerTagRow.markDeleted()
                    changed = true
                }
            }
        })

        if (changed) {
            self.TagsChanged.next(self)
        }
    }

    hasTag(TagId: number): boolean {
        var found = false

        _.forEach(this.drWB_Customer.WB_CustomerTagRelationshipRows, (aWBCustomerTagRow: WB_CustomerTagRelationshipRow) => {
            if (aWBCustomerTagRow && aWBCustomerTagRow.isActive && aWBCustomerTagRow.WB_TagRow) {
                if (aWBCustomerTagRow.WB_TagRow.cif.WB_TAG_ID == TagId) {
                    found = true
                    return false // stop iterating
                }
            }
        })

        return found
    }

    setDateRange(TagId: number, DateRange: IDateRange): void {
        _.forEach(this.drWB_Customer.WB_CustomerTagRelationshipRows, (aWBCustRow: WB_CustomerTagRelationshipRow) => {
            if (aWBCustRow && aWBCustRow.isActive && aWBCustRow.WB_TagRow) {
                if (aWBCustRow.WB_TagRow.cif.WB_TAG_ID == TagId) {
                    aWBCustRow.cif.EFFECTIVE_START_DT = DateRange.StartDt.getFullYear().toString() + ('0' + (DateRange.StartDt.getMonth() + 1)).slice(-2) + ('0' + DateRange.StartDt.getDate()).slice(-2)
                    aWBCustRow.cif.EFFECTIVE_END_DT = DateRange.EndDt.getFullYear().toString() + ('0' + (DateRange.EndDt.getMonth() + 1)).slice(-2) + ('0' + DateRange.EndDt.getDate()).slice(-2)
                    return
                }
            }
        })
    }

    getDateRange(TagId: number): IDateRange {
        let aDateRange: IDateRange = {}
        _.forEach(this.drWB_Customer.WB_CustomerTagRelationshipRows, (aWBCustRow: WB_CustomerTagRelationshipRow) => {
            if (aWBCustRow && aWBCustRow.isActive && aWBCustRow.WB_TagRow) {
                if (aWBCustRow.WB_TagRow.cif.WB_TAG_ID == TagId) {
                    aDateRange.StartDt = new Date(Number(aWBCustRow.cif.EFFECTIVE_START_DT.slice(0, 4)), Number(aWBCustRow.cif.EFFECTIVE_START_DT.slice(4, 6)) - 1, Number(aWBCustRow.cif.EFFECTIVE_START_DT.slice(6, 8)))
                    aDateRange.EndDt = new Date(Number(aWBCustRow.cif.EFFECTIVE_END_DT.slice(0, 4)), Number(aWBCustRow.cif.EFFECTIVE_END_DT.slice(4, 6)) - 1, Number(aWBCustRow.cif.EFFECTIVE_END_DT.slice(6, 8)))

                    return aDateRange
                }
            }
        })

        return aDateRange
    }


//</editor-fold>

    //<editor-fold desc = "Record Resolver --Static">

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {

        let opts = boLoadOptions || {}
        let dbContext: PAdbContext

        if ('dbContext' in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }
        const customer_options: CreateOptions = {
            standardConditions: {
                [esWB_Customer_Relationship]: {"DELETED_IND": {"notIn": ['1']}},
                [esWB_Customer_Tag_Relationship]: {"DELETED_IND": {"notIn": ['1']}},
                [esWB_Customer_SalesArea_Relationship]: {"DELETED_IND": {"notIn": ['1']}},
                [esWB_Customer_Role_Relationship]: {"DELETED_IND": {"notIn": ['1']}}
            },
            dctIncludeBelongsToNamedRelations: {
                [esWB_Customer_Role]: [esWB_Customer_Type],
                [esWB_SalesArea]: [esWB_SalesOrg, esWB_Division, esWB_DistribChannel]
            }
        }

        const aRecResolver = new ESRecordResolver(dbContext, customer_options)

        return aRecResolver;

    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {

        return new Promise<void>((resolve, error) => {
            recordResolver.findAllWithRelationships(esWB_Customer, ids).then((result) => {
                resolve()
            }).catch((err) => {
                error(err)
            })
        })
    }

    static concurrentLoadCountHint(): number {
        return 10;
    }

    //</editor-fold>


};


PAEnvironment.registerBusinessObjectClass(CustomerBO, "CustomerBO", "Customer")
